import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch } from "../../state/store";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { User } from "../../models/User";
import { Toast } from "../../components/Toast";
import { AgentCommissionRule } from "../../models/AgentCommissionRule";
import { CommissionRuleType } from "../../models/CommissionRuleType";
import { fetchAgencyUserCommissionRules, updateAgencyUserCommissionRule } from "../../state/agencies/action";
import { fetchCommissionRuleTypes } from "../../state/commissions/action";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../components/LoadingMask";
import { Form } from "../../components/form/Form";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormPhoneInput } from "../../components/form/ReactiveFormPhoneInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { getRoles, RoleEnum } from "../../enums/Role";
import { getBusiness } from "../../enums/Business";
import Button from "../../components/Button";
import { CheckIcon } from "@heroicons/react/24/outline";
import { UserRuleModal } from "../../components/agents/rules/UserRuleModal";
import { createUser, updateUser } from "../../state/users/actions";
import { UsersPath } from "../../constants/Routes";

export const UserForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentUser = useSelector((state: any) => state.users.currentUser);
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const isLoading = useSelector((state: any) => state.users.isLoading);
    const error = useSelector((state: any) => state.users.error);
    const [showError, setShowError] = useState(false);

    const stateUser: User | null = location.state ? location.state.user ?? null : null;
    const isNewUser = stateUser === null
    const [user, setUser] = useState<User | null>(stateUser)

     const [userRuleInfoModal, setUserRuleInfoModal] = useState<{
        show: boolean;
        user?: User;
        currentUser?: User;
        commissionRuleTypes: CommissionRuleType[];
    }>({ show: false, user: undefined, currentUser: undefined, commissionRuleTypes: [] });

    useEffect(() => {
        if (showError) {
          Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
          setShowError(false);
        }
    }, [showError, error, t]);
    
    const methods = useForm<User>({
        defaultValues: user !== null ? user : {}
    });
    const { handleSubmit, control } = methods;

    const [commissionRules, setCommissionRules] = useState<AgentCommissionRule[]>([]);
    const [commissionRuleTypes, setCommissionRuleTypes] = useState<CommissionRuleType[]>([]);
    const [isLoadingCommissionRules, setIsLoadingCommissionRules] = useState(false)

    useEffect(() => {
        if(user !== null && !isNewUser) {
            setIsLoadingCommissionRules(true);
            dispatch(
                fetchAgencyUserCommissionRules({
                    agencyId: currentUser.agency_id,
                    userId: user.id!,
                })
            ).then((e) => {
                setCommissionRules((e.payload || []) as AgentCommissionRule[]);
                setIsLoadingCommissionRules(false);
            });
        
            dispatch(
                fetchCommissionRuleTypes({
                    apiPath: "/commission-rule-types",
                    includeInactive: false,
                })
            ).then((x) =>
                setCommissionRuleTypes((x.payload as CommissionRuleType[]) ?? [])
            );
        }
    }, [user]);

    const setRuleAsDefault = (rule: AgentCommissionRule) => {
        if (rule.is_default) {
          return;
        }
    
        setCommissionRules((prev: AgentCommissionRule[]) => {
          prev.forEach((r: AgentCommissionRule) => (r.is_default = false));
          prev[
            prev.findIndex((x: AgentCommissionRule) => x.id === rule.id)
          ].is_default = true;
          return [...prev];
        });
    
        dispatch(
          updateAgencyUserCommissionRule({
            agencyId: currentUser.agency_id,
            userId: user!.id!,
            ruleId: rule.id!,
            payload: { is_default: true },
          })
        ).then((e) => {
          if (e.type === "agencies/updateAgencyUserCommissionRule/rejected") {
            setShowError(true);
          } else {
            toast(t("SAVED_SUCCESFULLY"));
            setCommissionRules((e.payload || []) as AgentCommissionRule[]);
          }
        });
    };

    const onSubmit: SubmitHandler<User> = async (data: User) => {
        if(user === null && isNewUser) {
            const updatedData = data;
            if (data.role_id) {
                updatedData.role_id = +data.role_id;
            }
            if (data.commission_level) {
                updatedData.commission_level = +data.commission_level;
            }
            if (data.business_id) {
                updatedData.business_id = +data.business_id;
            }
            
            dispatch(createUser({ user: updatedData, agencyId: currentUser.agency_id })).then((e) => {
                if (e.type === "users/createUser/rejected") {
                    setShowError(true);
                } else {
                    toast(t("SAVED_SUCCESFULLY"));
                    navigate(UsersPath);
                }
            });
        } else {
            const updatedData = data;
            if (data.role_id) {
                updatedData.role_id = +data.role_id;
            }
            if (data.commission_level) {
                updatedData.commission_level = +data.commission_level;
            }
            if (data.business_id) {
                updatedData.business_id = +data.business_id;
            }
            dispatch(updateUser({ user: updatedData, agencyId: currentUser.agency_id })).then((e) => {
                if (e.type === "users/updateUser/rejected") {
                    setShowError(true);
                } else {
                    toast(t("SAVED_SUCCESFULLY"));
                    navigate(UsersPath);
                }
            });
        }
    }

    return (
        <>
            <div className="w-full grow px-5 space-y-4 flex flex-col">
                {isLoading && <LoadingMask />}
                <ToastContainer progressStyle={{ background: "#D4AF37" }} />
                <UserRuleModal
                    info={userRuleInfoModal}
                    onClose={() => setUserRuleInfoModal({ show: false, user: undefined, currentUser: undefined, commissionRuleTypes: [] })}
                    onSubmited={(rules) => setCommissionRules(rules)}
                />
                <div className="text-black max-h-full p-5 w-screen md:w-full relative overflow-x-auto bg-white rounded-lg shadow transition-all duration-500 ease-in-out overflow-hidden">
                    <div className="text-xl font-bold text-black pl-3">{t("USER")}</div>
                    <div className="pb-5">
                        <Form handleOnSubmit={handleSubmit(onSubmit)}>
                            <div className="w-full text-lg font-semibold p-3">
                                {t("PERSONAL_INFORMATION")}
                            </div>
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("FIRST_NAME")}
                                name="first_name"
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("LAST_NAME")}
                                name="last_name"
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("SOCIAL_SECURITY_NUMBER")}
                                name="social_security_number"
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                type="date"
                                className="md:w-1/3"
                                label={t("DATE_OF_BIRTH")}
                                name="date_of_birth"
                                isRequired
                            />
                            <ReactiveFormPhoneInput
                                control={control}
                                className="md:w-1/3"
                                name="phone"
                                label={t("PHONE")}
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("EMAIL")}
                                name="email"
                                type="email"
                                isDisabled={user != null && !isNewUser}
                            />
                            {(user == null && isNewUser) && <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("PASSWORD")}
                                name={`password`}
                                isRequired
                            />}

                            <div className="w-full text-lg font-semibold p-3">
                                {t("AGENT_INFORMATION")}
                            </div>
                            <ReactiveFormSelect
                                width="md:w-1/3"
                                name={`role_id`}
                                label={t("ROLE")}
                                options={getRoles(currentUser?.role_id)}
                                control={control}
                                isRequired
                            />
                            <ReactiveFormSelect
                                width="md:w-1/3"
                                name={`business_id`}
                                label={t("BUSINESS")}
                                options={getBusiness()}
                                control={control}
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("NATIONAL_PRODUCER_NUMBER")}
                                name={`national_producer_number`}
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("AKA_LICENSE")}
                                name={`aka_license`}
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("UPLINE")}
                                name={`upline`}
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("OVERRIDES")}
                                name={`overrides`}
                            />
                            {/*<ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("COMMISSION_LEVEL")}
                                name={`commission_level`}
                                type="number"
                            />*/}

                            { (user !== null && !isNewUser) && (
                                <>
                                    {currentUser.role_id !== RoleEnum.AGENT && (
                                        <>
                                            <div className="w-full text-lg font-semibold p-3 flex justify-between">
                                                {t("COMMISSIONS")}
                                                <Button onClick={() => setUserRuleInfoModal({
                                                    show: true,
                                                    user: user,
                                                    currentUser: currentUser,
                                                    commissionRuleTypes: commissionRuleTypes
                                                })}>
                                                    <span className="flex items-center font-semibold pr-3">
                                                    <div className="w-8 p-1 aspect-square mr-2">
                                                        <CheckIcon />
                                                    </div>
                                                    {t("NEW_COMMISSION_RULE")}
                                                    </span>
                                                </Button>
                                            </div>
                                            {
                                                isLoadingCommissionRules &&
                                                <div className="w-full flex items-center justify-center pt-6 pb-5">
                                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-gold"></div>
                                                </div>
                                            }
                                            {
                                                !isLoadingCommissionRules &&
                                                <div className="px-3 grid grid-cols-3 gap-6">
                                                    {commissionRules.map((r) => (
                                                        <>
                                                        <div
                                                            onClick={() => setRuleAsDefault(r)}
                                                            className="py-4 px-6 max-w-sm mx-auto bg-white rounded-xl border hover:border-blue space-y-4 group cursor-pointer"
                                                        >
                                                            <div className="flex space-x-4 justify-between">
                                                            <div className="text-lg font-medium text-black">
                                                                {r.name}
                                                            </div>
                                
                                                            <div className="text-xl font-bold text-black px-3">
                                                                {r.is_relative
                                                                ? `${r.value! * 100}%`
                                                                : `$${r.value}`}
                                                            </div>
                                                            </div>
                                
                                                            {r.is_default && (
                                                            <span className="bg-blue text-white text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
                                                                {t("DEFAULT")}
                                                            </span>
                                                            )}
                                
                                                            {!r.is_default && (
                                                            <span className="bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full invisible group-hover:visible">
                                                                {t("SET_AS_DEFAULT")}
                                                            </span>
                                                            )}
                                
                                                            <p className="text-slate-500 text-xs">
                                                            {r.description}
                                                            </p>
                                                        </div>
                                                        </>
                                                    ))}
                                                </div>
                                            }
                                        </>)}
                        
                                        {currentUser.role_id === RoleEnum.AGENT && currentUser.id === user.id && (
                                        <>
                                            <div className="w-full text-lg font-semibold p-3 flex justify-between">
                                                {t("COMMISSIONS")}
                                            </div>
                                             {
                                                isLoadingCommissionRules &&
                                                <div className="w-full flex items-center justify-center pt-6 pb-5">
                                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-gold"></div>
                                                </div>
                                            }
                                            {
                                                !isLoadingCommissionRules &&
                                                <div className="px-3 grid grid-cols-3 gap-6">
                                                    {commissionRules.map((r) => (
                                                        <>
                                                        <div className="py-4 px-6 max-w-sm mx-auto bg-white rounded-xl border space-y-4">
                                                            <div className="flex space-x-4 justify-between">
                                                            <div className="text-lg font-medium text-black">
                                                                {r.name}
                                                            </div>
                                
                                                            <div className="text-xl font-bold text-black px-3">
                                                                {r.is_relative
                                                                ? `${r.value! * 100}%`
                                                                : `$${r.value}`}
                                                            </div>
                                                            </div>
                                
                                                            {r.is_default && (
                                                            <span className="bg-blue text-white text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
                                                                {t("DEFAULT")}
                                                            </span>
                                                            )}
                                
                                                            <p className="text-slate-500 text-xs">
                                                            {r.description}
                                                            </p>
                                                        </div>
                                                        </>
                                                    ))}
                                                </div>
                                            }
                                        </>)}
                                </>
                            )}
                        </Form>
                    </div>
                </div>
                <div className="pb-5 text-right">
                    <Button onClick={handleSubmit(onSubmit)}>
                    <span className="flex items-center font-semibold pr-3">
                        <div className="w-8 p-1 aspect-square mr-2">
                        <CheckIcon />
                        </div>
                        {t("SAVE")}
                    </span>
                    </Button>
                </div>
            </div>
        </>
    )
}